import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';
import { useDispatch, useSelector } from 'react-redux';

import { LoadingButton } from '@atlaskit/button';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/custom-theme-button';
import Form, { ErrorMessage } from '@atlaskit/form';
import IconInfo from '@atlaskit/icon/glyph/info';
import ModalDialog, {
  ModalBody,
  ModalHeader,
  ModalTitle,
} from '@atlaskit/modal-dialog';
import { Text } from '@atlaskit/primitives';
import Select from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { UsePipelineDefinitions } from 'src/components/pipelines/hooks/use-pipeline-definitions';
import { usePipelineVariables } from 'src/components/pipelines/hooks/use-pipeline-variables';
import { Repository } from 'src/components/pipelines/models';
import { requestBuildConfiguration } from 'src/components/pipelines/redux/actions/pipelines';
import { getYmlPath } from 'src/components/pipelines/redux/selectors/pipelines';
import { LoadingStatus } from 'src/constants/loading-status';
import { useStatsigGate } from 'src/contexts/feature-provider';

import {
  DOCS_CUSTOM_PIPELINE_URL,
  DOCS_SCHEDULED_PIPELINES,
  QUARTZ_URL,
} from '../const';
import {
  BranchOption,
  FetchBranches,
  HourlyEnum,
  PipelineDefinitionOption,
  ScheduleEnum,
  WeekdayEnum,
  DayOfMonthEnum,
} from '../types';
import parseSchedule, {
  toUTCDay,
  getDayWithSuffix,
  toUTCRange,
  toUTCDayOfMonth,
} from '../utils/parseSchedule';

import PipelineVariables from './PipelineVariables';
import RunPipelineBranchSelector from './RunPipelineBranchSelector';
import RunPipelineSelector from './RunPipelineSelector';
import messages from './scheduleMessages.i18n';
import {
  CustomSelectorWrapper,
  FooterButtons,
  Header,
  Information,
  Label,
  ScheduleGroup,
} from './styled';

const monthlyScheduleItems = Object.keys(DayOfMonthEnum).map(key => ({
  label: DayOfMonthEnum[key as keyof typeof DayOfMonthEnum],
  value: DayOfMonthEnum[key as keyof typeof DayOfMonthEnum],
}));
const weeklyScheduleItems = Object.keys(WeekdayEnum).map(key => ({
  label: key,
  value: key,
}));
const hourlyScheduleItems = Object.keys(HourlyEnum)
  .filter(isNaN as any)
  .map(key => ({ label: key, value: key }));

type Props = {
  fetchBranches: FetchBranches;
  onCloseDialog: (props: any) => void;
  showBlanket?: boolean;
  onCancelDialog: () => void;
  cronErrorMessage: string;
  scheduleStatus: string;
  usePipelineDefinitionsHook?: (
    repository: Repository,
    target: string
  ) => UsePipelineDefinitions;
};
type DayOfMonthType = (typeof DayOfMonthEnum)[keyof typeof DayOfMonthEnum];

const CreateScheduleModal = ({
  onCloseDialog,
  fetchBranches,
  showBlanket,
  onCancelDialog,
  cronErrorMessage,
  scheduleStatus,
  usePipelineDefinitionsHook,
}: Props) => {
  const dispatch = useDispatch();

  const ymlPath = useSelector(getYmlPath);

  const [branchOption, setBranchOption] = useState<BranchOption>();
  const [pipelineDefinitionOption, setPipelineDefinitionOption] =
    useState<PipelineDefinitionOption>();
  const [schedule, setSchedule] = useState<ScheduleEnum>(ScheduleEnum.Hourly);
  const [weekday, setWeekday] = useState<WeekdayEnum>(WeekdayEnum.Monday);
  const [hour, setHour] = useState<HourlyEnum>(HourlyEnum['00:00 - 01:00']);
  const [dayOfMonth, setDayOfMonth] = useState<DayOfMonthType>(
    DayOfMonthEnum.Day1
  );
  const [customCronPattern, setCustomCronPattern] = useState<string>('');
  const isBitbucketPipelinesYmlSupportEnabled = useStatsigGate(
    StatsigFeatureKeys.isBitbucketPipelinesYmlSupportEnabled
  ) as boolean;
  const isPassVariableInSchedulePipelineEnabled = useStatsigGate(
    StatsigFeatureKeys.isPassVariableInSchedulePipelineEnabled
  ) as boolean;
  const { variables, selector, setVariablesValues, filteredVariables } =
    usePipelineVariables(pipelineDefinitionOption);

  const scheduleItems = Object.keys(ScheduleEnum).map(key => ({
    label: key,
    value: ScheduleEnum[key as keyof typeof ScheduleEnum],
  }));

  const onCreateSchedule = useCallback(
    (_formData: {}) => {
      if (!branchOption || !pipelineDefinitionOption) {
        return;
      }

      onCloseDialog({
        target: {
          type: 'pipeline_ref_target',
          selector,
          ref_name: branchOption.branch.name,
          ref_type: 'branch',
        },
        cron_pattern: parseSchedule({
          schedule,
          weekday,
          hour,
          dayOfMonth,
          customCronPattern,
        }),
        enabled: true,
        ...filteredVariables,
      });
    },
    [
      branchOption,
      pipelineDefinitionOption,
      onCloseDialog,
      schedule,
      weekday,
      hour,
      dayOfMonth,
      customCronPattern,
      filteredVariables,
      selector,
    ]
  );

  const onScheduleChange = (item: any) => {
    setSchedule(item.value);
    setDayOfMonth(DayOfMonthEnum.Day1);
  };

  const onDayOfMonthChange = (item: any) => setDayOfMonth(item.value);

  const onWeekdayChange = (item: any) => setWeekday(item.value);

  const onHourChange = (item: any) => setHour(item.value);

  const onCronPatternChange = useCallback((event: any) => {
    setCustomCronPattern(event.target.value);
  }, []);

  const isDisabled = useMemo(
    () =>
      !pipelineDefinitionOption || scheduleStatus === LoadingStatus.Fetching,
    [pipelineDefinitionOption, scheduleStatus]
  );

  const target = useMemo(
    () => (branchOption ? branchOption.branch.revision : ''),
    [branchOption]
  );

  useEffect(() => {
    if (target) {
      dispatch(
        requestBuildConfiguration(target, isBitbucketPipelinesYmlSupportEnabled)
      );
    }
  }, [target, isBitbucketPipelinesYmlSupportEnabled, dispatch]);

  const getScheduleMessage = (
    newSchedule: ScheduleEnum,
    localHour: HourlyEnum,
    localWeekday: WeekdayEnum,
    localDayOfMonth: DayOfMonthType
  ) => {
    const utcRange = toUTCRange(localHour);

    switch (newSchedule) {
      case ScheduleEnum.Daily: {
        return (
          <FormattedMessage {...messages.dailyMessage} values={{ utcRange }} />
        );
      }

      case ScheduleEnum.Weekly: {
        const utcDay = toUTCDay(localHour, localWeekday);
        return (
          <FormattedMessage
            {...messages.weeklyMessage}
            values={{ utcDay, utcRange }}
          />
        );
      }

      case ScheduleEnum.Monthly: {
        const UTCdayOfMonth = toUTCDayOfMonth(localHour, localDayOfMonth);
        const dayOfMonthUTC =
          UTCdayOfMonth === 'L' ? 'last day' : UTCdayOfMonth;
        const daySuffix = getDayWithSuffix(dayOfMonthUTC);
        const timeRange = toUTCRange(localHour);
        return (
          <FormattedMessage
            {...messages.monthlyMessage}
            values={{ timeRange, daySuffix }}
          />
        );
      }
      case ScheduleEnum.Custom: {
        return (
          <FormattedMessage
            {...messages.customMessage}
            values={{
              quartzLink: (
                <Button
                  appearance="link"
                  href={QUARTZ_URL}
                  spacing="none"
                  target="_blank"
                >
                  <FormattedMessage {...messages.quartzLinkMessage} />
                </Button>
              ),
            }}
          />
        );
      }
      default: {
        return null;
      }
    }
  };
  return (
    <ModalDialog
      onClose={onCloseDialog}
      width="medium"
      shouldScrollInViewport
      isBlanketHidden={!showBlanket}
    >
      <ModalHeader>
        <ModalTitle>
          <Header>
            Create a schedule
            <Information>
              <Button
                iconBefore={<IconInfo label="Information" />}
                href={DOCS_SCHEDULED_PIPELINES}
                appearance="subtle-link"
                spacing="none"
                target="_blank"
              />
            </Information>
          </Header>
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        <p data-testid="schedule-modal">
          Schedule a{' '}
          <Button
            href={DOCS_CUSTOM_PIPELINE_URL}
            spacing="none"
            appearance="link"
            target="_blank"
          >
            pipeline
          </Button>{' '}
          to run at an interval.
        </p>
        <Form onSubmit={onCreateSchedule}>
          {({ formProps }) => (
            <form {...formProps}>
              <RunPipelineBranchSelector
                branchOption={branchOption}
                fetchBranches={fetchBranches}
                onChange={setBranchOption}
              />
              <RunPipelineSelector
                usePipelineDefinitionsHook={usePipelineDefinitionsHook}
                pipelineDefinitionOption={pipelineDefinitionOption}
                configurationUrl={ymlPath}
                target={target}
                onChange={setPipelineDefinitionOption}
              />
              {isPassVariableInSchedulePipelineEnabled && variables.length ? (
                <PipelineVariables
                  variables={variables}
                  onChange={setVariablesValues}
                />
              ) : null}
              <Label>Schedule (Local time)</Label>
              <ScheduleGroup>
                <Select
                  isDisabled={isDisabled}
                  options={scheduleItems}
                  onChange={onScheduleChange}
                  defaultValue={scheduleItems[0]}
                  name="schedule"
                  isSearchable={false}
                  id="create-schedule-selector-select"
                  instanceId="create-schedule-selector-select"
                />
                {schedule === ScheduleEnum.Monthly && (
                  <Select
                    isDisabled={isDisabled}
                    options={monthlyScheduleItems}
                    onChange={onDayOfMonthChange}
                    defaultValue={monthlyScheduleItems[0]}
                    name="dayOfMonth"
                    placeholder="1"
                    isSearchable={false}
                    menuPlacement="top"
                  />
                )}
                {schedule === ScheduleEnum.Weekly && (
                  <Select
                    isDisabled={isDisabled}
                    options={weeklyScheduleItems}
                    onChange={onWeekdayChange}
                    defaultValue={weeklyScheduleItems[0]}
                    name="weekday"
                    placeholder="Monday"
                    isSearchable={false}
                    menuPlacement="top"
                  />
                )}
                {![ScheduleEnum.Hourly, ScheduleEnum.Custom].includes(
                  schedule
                ) && (
                  <Select
                    isDisabled={isDisabled}
                    options={hourlyScheduleItems}
                    onChange={onHourChange}
                    defaultValue={hourlyScheduleItems[0]}
                    name="hour"
                    placeholder="00:00 - 01:00"
                    isSearchable={false}
                    maxMenuHeight={250}
                    menuPlacement="top"
                  />
                )}
              </ScheduleGroup>
              {schedule === ScheduleEnum.Custom && (
                <CustomSelectorWrapper>
                  <Label>
                    <FormattedMessage {...messages.customLabel} />
                  </Label>
                  <Textfield
                    name="custom"
                    onChange={onCronPatternChange}
                    isDisabled={isDisabled}
                    testId="custom"
                  />
                  {cronErrorMessage && (
                    <ErrorMessage testId="cron-error-message">
                      <FormattedMessage
                        {...messages.errorMessage}
                        values={{
                          message: cronErrorMessage,
                        }}
                      />
                    </ErrorMessage>
                  )}
                </CustomSelectorWrapper>
              )}

              <Text>
                {getScheduleMessage(schedule, hour, weekday, dayOfMonth)}
              </Text>
              <FooterButtons>
                <ButtonGroup>
                  <Button appearance="subtle" onClick={onCancelDialog}>
                    Cancel
                  </Button>
                  <LoadingButton
                    isDisabled={isDisabled}
                    appearance="primary"
                    type="submit"
                    isLoading={scheduleStatus === LoadingStatus.Fetching}
                  >
                    Create
                  </LoadingButton>
                </ButtonGroup>
              </FooterButtons>
            </form>
          )}
        </Form>
      </ModalBody>
    </ModalDialog>
  );
};

export default memo(CreateScheduleModal);
